export const FIELD_TYPES = [
  {
    type: 'text',
    labelKey: 'TEXT',
    icon: 'i-lucide-type',
    iconClasses: 'bg-n-orange-3 text-n-orange-11',
  },
  {
    type: 'textarea',
    labelKey: 'TEXTAREA',
    icon: 'i-lucide-text',
    iconClasses: 'bg-n-blue-3 text-n-blue-11',
  },
  {
    type: 'number',
    labelKey: 'NUMBER',
    icon: 'i-lucide-hash',
    iconClasses: 'bg-n-teal-3 text-n-teal-11',
  },
  {
    type: 'date',
    labelKey: 'DATE',
    icon: 'i-lucide-calendar',
    iconClasses: 'bg-n-iris-3 text-n-iris-11',
  },
  {
    type: 'select',
    labelKey: 'SELECT',
    icon: 'i-lucide-circle-dot',
    iconClasses: 'bg-n-ruby-3 text-n-ruby-11',
  },
  {
    type: 'checkbox',
    labelKey: 'CHECKBOX',
    icon: 'i-lucide-list-checks',
    iconClasses: 'bg-n-teal-3 text-n-teal-11',
  },
  {
    type: 'attachment',
    labelKey: 'ATTACHMENT',
    icon: 'i-lucide-paperclip',
    iconClasses: 'bg-n-blue-3 text-n-blue-11',
  },
];

export const fieldMeta = type =>
  FIELD_TYPES.find(meta => meta.type === type) || FIELD_TYPES[0];

export const isTextLike = type =>
  ['text', 'textarea', 'contact'].includes(type);

export const hasOptions = type => ['select', 'checkbox'].includes(type);

export const generateId = (prefix = 'fl') => {
  const unique =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}_${unique}`;
};

const newOption = label => ({ id: generateId('o'), label });

const defaultOptions = t => [
  newOption(`${t('DYNAMIC_FORMS.PROPERTIES.OPTION')} 1`),
  newOption(`${t('DYNAMIC_FORMS.PROPERTIES.OPTION')} 2`),
];

const textLikeDefaults = () => ({ placeholder: '', minChars: 0, maxChars: 0 });

const FIELD_DEFAULTS = {
  text: textLikeDefaults,
  textarea: textLikeDefaults,
  contact: t => ({
    ...textLikeDefaults(),
    label: t('DYNAMIC_FORMS.FIELD_DEFAULT.CONTACT_LABEL'),
    placeholder: '(00) 00000-0000',
  }),
  number: () => ({ placeholder: '', numMin: '', numMax: '' }),
  date: () => ({ includeTime: false }),
  select: t => ({ style: 'dropdown', options: defaultOptions(t) }),
  checkbox: t => ({ options: defaultOptions(t), minSel: 0, maxSel: 0 }),
  attachment: () => ({
    minAtt: 1,
    maxAtt: 5,
    types: 'PNG, JPG, PDF',
    maxSize: 10,
  }),
};

export const createField = (type, t) => {
  const base = {
    id: generateId(),
    type,
    label: t(`DYNAMIC_FORMS.FIELD_TYPES.${fieldMeta(type).labelKey}`),
    help: '',
    required: false,
    cond: { on: false, field: '', op: 'eq', value: '' },
  };
  const extras = FIELD_DEFAULTS[type];
  return extras ? { ...base, ...extras(t) } : base;
};

export const duplicateField = field => {
  const copy = JSON.parse(JSON.stringify(field));
  copy.id = generateId();
  if (copy.options) {
    copy.options = copy.options.map(option => ({
      ...option,
      id: generateId('o'),
    }));
  }
  return copy;
};

const summarizeTextLike = (field, t) =>
  field.minChars > 0
    ? [t('DYNAMIC_FORMS.SUMMARY.MIN_CHARS', { count: field.minChars })]
    : [];

const FIELD_SUMMARIES = {
  text: summarizeTextLike,
  textarea: summarizeTextLike,
  contact: summarizeTextLike,
  select: (field, t) => {
    const style =
      field.style === 'radio'
        ? t('DYNAMIC_FORMS.SUMMARY.RADIO')
        : t('DYNAMIC_FORMS.SUMMARY.DROPDOWN');
    return [
      `${style} · ${t('DYNAMIC_FORMS.SUMMARY.OPTIONS', {
        count: field.options.length,
      })}`,
    ];
  },
  checkbox: (field, t) => {
    const parts = [
      t('DYNAMIC_FORMS.SUMMARY.OPTIONS', { count: field.options.length }),
    ];
    if (field.minSel > 0) {
      parts.push(t('DYNAMIC_FORMS.SUMMARY.MIN_SEL', { count: field.minSel }));
    }
    return parts;
  },
  attachment: (field, t) => [
    t('DYNAMIC_FORMS.SUMMARY.ATTACHMENTS', {
      min: field.minAtt,
      max: field.maxAtt,
    }),
  ],
  number: field => {
    const parts = [];
    if (field.numMin !== '' && field.numMin !== undefined) {
      parts.push(`≥ ${field.numMin}`);
    }
    if (field.numMax !== '' && field.numMax !== undefined) {
      parts.push(`≤ ${field.numMax}`);
    }
    return parts;
  },
  date: (field, t) =>
    field.includeTime ? [t('DYNAMIC_FORMS.SUMMARY.WITH_TIME')] : [],
};

export const fieldSummary = (field, t) => {
  const parts = [
    field.required
      ? t('DYNAMIC_FORMS.SUMMARY.REQUIRED')
      : t('DYNAMIC_FORMS.SUMMARY.OPTIONAL'),
  ];
  const summarize = FIELD_SUMMARIES[field.type];
  if (summarize) parts.push(...summarize(field, t));
  return parts.join('  ·  ');
};

const validateTextLike = (field, value) => {
  const length = typeof value === 'string' ? value.trim().length : 0;
  if (length > 0 && field.minChars > 0 && length < field.minChars) {
    return {
      key: 'DYNAMIC_FORM_MIN_CHARS_ERROR',
      params: { count: field.minChars },
    };
  }
  if (field.maxChars > 0 && length > field.maxChars) {
    return {
      key: 'DYNAMIC_FORM_MAX_CHARS_ERROR',
      params: { count: field.maxChars },
    };
  }
  return null;
};

const FIELD_VALIDATORS = {
  text: validateTextLike,
  textarea: validateTextLike,
  contact: validateTextLike,
  number: (field, value) => {
    if (value === '' || value === undefined || value === null) return null;
    const number = Number(value);
    if (
      field.numMin !== '' &&
      field.numMin !== undefined &&
      number < Number(field.numMin)
    ) {
      return {
        key: 'DYNAMIC_FORM_NUM_MIN_ERROR',
        params: { count: field.numMin },
      };
    }
    if (
      field.numMax !== '' &&
      field.numMax !== undefined &&
      number > Number(field.numMax)
    ) {
      return {
        key: 'DYNAMIC_FORM_NUM_MAX_ERROR',
        params: { count: field.numMax },
      };
    }
    return null;
  },
  checkbox: (field, value) => {
    const count = Array.isArray(value) ? value.length : 0;
    if (field.minSel > 0 && count < field.minSel) {
      return {
        key: 'DYNAMIC_FORM_MIN_SEL_ERROR',
        params: { count: field.minSel },
      };
    }
    if (field.maxSel > 0 && count > field.maxSel) {
      return {
        key: 'DYNAMIC_FORM_MAX_SEL_ERROR',
        params: { count: field.maxSel },
      };
    }
    return null;
  },
  attachment: (field, value) => {
    const count = Array.isArray(value) ? value.length : 0;
    if (field.minAtt > 0 && count < field.minAtt) {
      return {
        key: 'DYNAMIC_FORM_MIN_ATT_ERROR',
        params: { count: field.minAtt },
      };
    }
    if (field.maxAtt > 0 && count > field.maxAtt) {
      return {
        key: 'DYNAMIC_FORM_MAX_ATT_ERROR',
        params: { count: field.maxAtt },
      };
    }
    return null;
  },
};

export const fieldValidationError = (field, value) => {
  const validate = FIELD_VALIDATORS[field.type];
  return validate ? validate(field, value) : null;
};

export const isFieldVisible = (field, answers = {}) => {
  const cond = field.cond;
  if (!cond || !cond.on || !cond.field) return true;

  const reference = answers[cond.field];
  if (cond.op === 'filled') {
    return (
      reference !== undefined &&
      reference !== '' &&
      !(Array.isArray(reference) && reference.length === 0)
    );
  }
  const value = reference === undefined ? '' : String(reference);
  if (cond.op === 'eq') return value === cond.value;
  if (cond.op === 'neq') return value !== cond.value;
  return true;
};
